import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, MessageCircle, Menu, X, User as UserIcon, LogOut } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useUser } from "../contexts/UserContext";
import { WHATSAPP_LINK } from "../lib/api";
import { Logo } from "./Logo";

const NAV = [
  { to: "/", label: "Inicio", end: true },
  { to: "/papeleria", label: "Papelería" },
  { to: "/3d", label: "Figuras 3D" },
  { to: "/cotizar", label: "Cotizar" },
  { to: "/contacto", label: "Contacto" },
];

export const Header = () => {
  const { totalCount } = useCart();
  const { user, logout } = useUser();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-cream/90 backdrop-blur-xl border-b-2 border-black"
          : "bg-cream border-b-2 border-black"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-24 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="shrink-0">
          <Logo size="large" />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `link-underline text-sm font-heading font-semibold tracking-wide transition-colors ${
                  isActive ? "text-mint-deep" : "text-brand-ink hover:text-mint-deep"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1 sm:gap-2">
          <a
            href={WHATSAPP_LINK()}
            target="_blank"
            rel="noreferrer"
            data-testid="header-whatsapp-btn"
            className="hidden sm:inline-flex items-center justify-center h-10 w-10 rounded-full text-brand-ink hover:bg-yolk-light transition"
            aria-label="WhatsApp"
          >
            <MessageCircle className="h-5 w-5" />
          </a>

          {user ? (
            <div className="relative">
              <button
                onClick={() => setUserMenu((s) => !s)}
                data-testid="header-user-menu"
                className="hidden sm:inline-flex items-center gap-2 px-3 h-10 rounded-full bg-mint-light text-brand-ink hover:bg-mint transition border border-black/10"
              >
                <UserIcon className="h-4 w-4" />
                <span className="text-xs font-medium max-w-[80px] truncate">{user.nombre}</span>
              </button>
              {userMenu && (
                <div className="absolute right-0 top-12 bg-white border-2 border-black w-56 z-50">
                  <Link
                    to="/mis-pedidos"
                    onClick={() => setUserMenu(false)}
                    data-testid="user-menu-orders"
                    className="block px-4 py-3 text-sm hover:bg-mint-light border-b border-gray-100"
                  >
                    Mis pedidos
                  </Link>
                  <button
                    onClick={() => { logout(); setUserMenu(false); navigate("/"); }}
                    data-testid="user-menu-logout"
                    className="w-full text-left px-4 py-3 text-sm hover:bg-yolk-light flex items-center gap-2"
                  >
                    <LogOut className="h-3 w-3" /> Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="header-login-btn"
              className="hidden sm:inline-flex items-center justify-center h-10 px-4 text-xs font-heading font-semibold text-brand-ink hover:text-mint-deep transition"
            >
              Ingresar
            </Link>
          )}

          <button
            onClick={() => navigate("/carrito")}
            data-testid="header-cart-btn"
            className="relative inline-flex items-center justify-center h-10 w-10 rounded-full text-brand-ink hover:bg-yolk-light transition"
            aria-label="Carrito"
          >
            <ShoppingBag className="h-5 w-5" />
            {totalCount > 0 && (
              <span
                data-testid="cart-badge"
                className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-yolk text-black text-[10px] font-bold flex items-center justify-center border border-black"
              >
                {totalCount}
              </span>
            )}
          </button>

          <button
            onClick={() => setMobileOpen((s) => !s)}
            data-testid="mobile-menu-toggle"
            className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full text-brand-ink hover:bg-yolk-light transition"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-nav" className="md:hidden bg-cream border-t-2 border-black px-6 py-6 space-y-3">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setMobileOpen(false)}
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `block text-lg font-heading font-bold ${isActive ? "text-mint-deep" : "text-brand-ink"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <div className="border-t-2 border-black pt-4 mt-4 flex flex-col gap-2">
            {user ? (
              <>
                <Link to="/mis-pedidos" onClick={() => setMobileOpen(false)} className="text-sm">Mis pedidos</Link>
                <button onClick={() => { logout(); setMobileOpen(false); }} className="text-left text-sm text-red-600">Cerrar sesión</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setMobileOpen(false)} className="text-sm font-heading font-semibold">Ingresar</Link>
                <Link to="/registro" onClick={() => setMobileOpen(false)} className="text-sm font-heading font-semibold text-mint-deep">Crear cuenta</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
